<template>
  <div class="home">
    <h2>Testers Emails</h2>

    <b-button @click="raisePopup({ target: $event.target })" variant="primary">
      Add new
    </b-button>

    <!-- Info modal -->
    <b-modal
      id="testerEmailModal"
      v-bind:title="this.isEdit ? 'Edit tester email' : 'New tester email'"
      @ok="testerEmailFormSubmit"
    >
      <b-form>
        <input id="newTesterEmailInput0" type="hidden" v-model="form.id" />
        <b-form-group
          id="newTesterEmailInputGroup1"
          label="Email:"
          label-for="newTesterEmailInput1"
        >
          <b-form-input
            id="newTesterEmailInput1"
            type="text"
            v-model="form.email"
            placeholder="Enter email"
          />
        </b-form-group>

        <b-form-group id="newTesterEmailGroup4">
          <b-form-checkbox v-model="formIsActive"
            >Is email active?</b-form-checkbox
          >
        </b-form-group>
      </b-form>
    </b-modal>

    <b-container fluid>
      <b-row>
        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="2" label="Sort" class="mb-0">
            <b-input-group>
              <b-form-select v-model="sortBy" :options="sortOptions">
                <option slot="first" :value="null">-- none --</option>
              </b-form-select>
              <b-form-select
                :disabled="!sortBy"
                v-model="sortDesc"
                slot="append"
              >
                <option :value="false">Asc</option>
                <option :value="true">Desc</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="3" label="Per page" class="mb-0">
            <b-form-select :options="pageOptions" v-model="perPage" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Main table element -->
      <b-table
        show-empty
        small
        hover
        stacked="md"
        :items="items"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :filter="filter"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @filtered="onFiltered"
        ref="testerEmailsTable"
        class="testerEmailsTable"
      >
        <template v-slot:cell(actions)="row">
          <b-button
            size="sm"
            class="m-1"
            @click="row.toggleDetails"
            variant="info"
          >
            …
          </b-button>
          <b-button
            size="sm"
            class="m-1"
            variant="warning"
            @click="
              raisePopup({
                target: $event.target,
                isEdit: true,
                testerEmail: row.item
              })
            "
          >
            ✎
          </b-button>
          <b-button
            size="sm"
            variant="danger"
            class="m-1 ml-3"
            @click="
              removeTesterEmail({
                target: $event.target,
                testerEmail: row.item
              })
            "
          >
            X
          </b-button>
        </template>
        <template v-slot:cell(active)="row">
          {{ row.value ? 'Yes' : 'No' }}
        </template>

        <template v-slot:row-details="row">
          <b-card>
            <ul>
              <li v-for="(value, key) in row.item" :key="key">
                {{ key }}: {{ value }}
              </li>
            </ul>
          </b-card>
        </template>
      </b-table>

      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination
            :total-rows="totalRows"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
/* eslint-disable no-magic-numbers */

const originalForm = {
  id: null,
  email: null,
  active: null
};

export default {
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      pageOptions: [20, 50, 100, 500],
      sortBy: null,
      sortDesc: false,
      filter: null,
      form: {
        ...originalForm
      },
      isEdit: false
    };
  },
  computed: {
    formIsActive: {
      get: function() {
        return !!this.form.active;
      },
      set: function(newValue) {
        this.form.active = newValue;
      }
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    },
    fields() {
      return [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          sortDirection: 'desc'
        },
        {
          key: 'email',
          label: 'Email',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'hash',
          label: 'Email hash',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'active',
          label: 'Email is active',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'createdAt',
          label: 'Created',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'updatedAt',
          label: 'Updated',
          sortable: true,
          class: 'text-center'
        },
        { key: 'actions', label: 'Actions' }
      ];
    },
    totalRows() {
      return this.$store.state.testerEmails.count;
    }
  },
  methods: {
    raisePopup({ target, isEdit = false, testerEmail = null }) {
      this.isEdit = isEdit;
      if (testerEmail && isEdit) {
        this.form = Object.keys(originalForm).reduce(
          (acc, current) =>
            testerEmail[current]
              ? {
                  ...acc,
                  [current]: String(testerEmail[current])
                }
              : acc,
          {}
        );
      } else {
        this.testerEmailFormReset();
      }
      this.$root.$emit('bv::show::modal', 'testerEmailModal', target);
    },
    removeTesterEmail({ testerEmail = null }) {
      if (!testerEmail) {
        return;
      }
      const promise = this.$store.dispatch('removeTesterEmail', testerEmail.id);

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        this.$refs.testerEmailsTable.refresh();
      });
    },
    onFiltered() {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.currentPage = 1;
    },
    items(ctx) {
      const promise = this.$store.dispatch('getTesterEmails', ctx);

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        const items = this.$store.state.testerEmails.rows.map(email => {
          if (email.active) {
            email._rowVariant = 'success';
          }
          return email;
        });
        // Must return an array of items or an empty array if an error occurred
        return items || [];
      });
    },
    testerEmailFormReset() {
      this.form = { ...originalForm };
    },
    async testerEmailFormSubmit(event) {
      if (!this.isEdit) {
        await this.$store.dispatch('addTesterEmail', {
          testerEmail: this.form,
          event
        });
      } else {
        await this.$store.dispatch('editTesterEmail', {
          testerEmail: this.form,
          event
        });
      }

      this.testerEmailFormReset();
      this.$refs.testerEmailsTable.refresh();
    }
  }
};
</script>
<style scoped type="text/scss">
.block {
  border-bottom: #dadada 1px solid;
  padding-bottom: 1em;
  margin-bottom: 3em;
}
.container {
  max-width: none;
}
.testerEmailsTable {
  font-size: 0.8rem;
}
.btn {
  font-size: 0.8rem;
}

td[data-label='Actions'] {
  white-space: nowrap;
}
</style>
